// frontend/src/components/dashboard/TopPerformersRanking.tsx
import React from 'react';
import { TrophyIcon, UserIcon } from '@heroicons/react/24/solid';

interface TopPerformer {
  id: number;
  nombre: string;
  total_ventas?: number;
  total_tickets?: number;
  comision?: number;
  avatar?: string;
}

interface TopPerformersRankingProps {
  title: string;
  performers: TopPerformer[];
  type: 'vendedoras' | 'modistas';
  loading?: boolean;
  onExpand?: () => void;
}

export const TopPerformersRanking: React.FC<TopPerformersRankingProps> = ({
  title,
  performers,
  type,
  loading = false,
  onExpand
}) => {
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
      minimumFractionDigits: 2
    }).format(value);
  };

  const getMedalColor = (index: number) => {
    switch (index) {
      case 0:
        return 'text-yellow-500'; // Oro
      case 1:
        return 'text-gray-400'; // Plata
      case 2:
        return 'text-orange-600'; // Bronce
      default:
        return 'text-gray-300';
    }
  };

  const getInitials = (nombre: string | null | undefined) => {
    if (!nombre) return '??';
    return nombre
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex items-center gap-2 mb-6">
          <TrophyIcon className="w-6 h-6 text-yellow-500" />
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
        </div>
        <div className="space-y-4">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="flex items-center gap-4 p-3 animate-pulse">
              <div className="w-12 h-12 bg-gray-200 rounded-full"></div>
              <div className="flex-1">
                <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                <div className="h-3 bg-gray-200 rounded w-1/2"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (!performers || performers.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex items-center gap-2 mb-6">
          <TrophyIcon className="w-6 h-6 text-yellow-500" />
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
        </div>
        <div className="text-center py-8 text-gray-500">
          <UserIcon className="w-12 h-12 mx-auto mb-2 text-gray-300" />
          <p>No hay datos disponibles</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <TrophyIcon className="w-6 h-6 text-yellow-500" />
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
        </div>
        {onExpand && (
          <button onClick={onExpand} className="text-sm text-blue-600 hover:text-blue-800">
            Ver todo
          </button>
        )}
      </div>

      <div className="space-y-3">
        {performers.slice(0, 5).map((performer, index) => (
          <div
            key={performer.id}
            className={`flex items-center gap-4 p-3 rounded-lg transition-all duration-200 ${index < 3
              ? 'bg-gradient-to-r from-gray-50 to-white border border-gray-200 hover:shadow-md'
              : 'bg-gray-50 hover:bg-gray-100'
              }`}
          >
            {/* Ranking Number / Medal */}
            <div className="flex items-center justify-center w-8">
              {index < 3 ? (
                <TrophyIcon className={`w-6 h-6 ${getMedalColor(index)}`} />
              ) : (
                <span className="text-lg font-bold text-gray-400">#{index + 1}</span>
              )}
            </div>

            {/* Avatar */}
            <div className="relative">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center text-white font-semibold text-sm shadow-md">
                {getInitials(performer.nombre)}
              </div>

            </div>

            {/* Info */}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-gray-900 truncate">
                {performer.nombre}
              </p>
              <div className="flex items-center gap-3 mt-1">
                {type === 'vendedoras' ? (
                  <>
                    <span className="text-xs text-gray-600">
                      {performer.total_ventas || 0} ventas
                    </span>
                    {performer.comision && (
                      <span className="text-xs font-medium text-green-600">
                        {formatCurrency(performer.comision)}
                      </span>
                    )}
                  </>
                ) : (
                  <>
                    <span className="text-xs text-gray-600">
                      {performer.total_tickets || 0} trabajos
                    </span>
                    {performer.comision && (
                      <span className="text-xs font-medium text-green-600">
                        {formatCurrency(performer.comision)}
                      </span>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* Score indicator */}
            {index < 3 && (
              <div className="flex flex-col items-end">
                <div className="text-xs font-medium text-gray-500">
                  {type === 'vendedoras' ? 'Comisión' : 'Ganancia'}
                </div>
                <div className={`text-sm font-bold ${index === 0 ? 'text-yellow-600' :
                  index === 1 ? 'text-gray-600' :
                    'text-orange-600'
                  }`}>
                  {performer.comision ? formatCurrency(performer.comision) : 'N/A'}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* View All Link */}
      {performers.length > 5 && (
        <button className="w-full mt-4 text-sm text-blue-600 hover:text-blue-700 font-medium py-2 border-t border-gray-200">
          Ver todos ({performers.length})
        </button>
      )}
    </div>
  );
};
